import { useEffect } from "react";

interface SeoProps {
  title: string;
  description: string;
  /** Absolute canonical URL for this page, e.g. https://silverjarz.com/visa-oman */
  canonical?: string;
  /** Open Graph / Twitter image URL, absolute if possible */
  image?: string;
  /** Open Graph type. Defaults to "website". */
  ogType?: string;
  /**
   * When true, adds <meta name="robots" content="noindex, nofollow">.
   * Use for private/account/admin pages that must never be indexed.
   * Never rely on this alone for access control — see Phase 1 for that.
   */
  noindex?: boolean;
  /**
   * One or more JSON-LD structured data objects (Organization, BreadcrumbList,
   * FAQPage, etc). Each is rendered as its own <script type="application/ld+json">
   * tag in <head>, removed again on unmount/prop change.
   */
  jsonLd?: object | object[];
}

const SITE_NAME = "SilverJarz";

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function removeMeta(attr: "name" | "property", key: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (el) el.remove();
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * useSeo — sets document.title + meta description/OG/Twitter/robots tags and
 * JSON-LD structured data for the current page, restoring/removing what it
 * added on unmount so navigating to a page that doesn't call useSeo doesn't
 * keep stale tags around.
 *
 * This exists instead of react-helmet-async because this environment has no
 * network access to `npm install` a new dependency — it's a minimal,
 * dependency-free stand-in.
 *
 * SPA LIMITATION (see Phase 3 report section F for full detail): all of this
 * runs client-side, after React mounts, via useEffect. A crawler that does
 * not execute JavaScript sees only the static <title>React App</title> and
 * generic description from public/index.html — it never sees any of what
 * this hook sets. Google's crawler does execute JS and generally picks this
 * up, but with a delay (separate rendering pass), and other crawlers/social
 * media unfurlers (e.g. some link-preview bots) may not execute JS at all.
 */
export function useSeo({ title, description, canonical, image, ogType = "website", noindex = false, jsonLd }: SeoProps) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", ogType);
    setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    if (canonical) {
      setCanonical(canonical);
      setMeta("property", "og:url", canonical);
    }
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
    }

    if (noindex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      removeMeta("name", "robots");
    }

    const addedScripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((obj) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.text = JSON.stringify(obj);
        document.head.appendChild(script);
        addedScripts.push(script);
      });
    }

    return () => {
      document.title = prevTitle;
      removeMeta("name", "robots");
      addedScripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, ogType, noindex, JSON.stringify(jsonLd)]);
}

export default useSeo;
